import type { CustomCellEditorProps } from 'ag-grid-react';
import { useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Checkbox, TruncateText } from '@mint-lib/tw-components';
import {
  ComboboxItem,
  ComboboxMenu,
  ComboboxMenuButton,
} from '@mint-lib/tw-components/patterns';

export type FlatSelectOption = {
  value: string;
  label: string;
};

export interface FlatSelectEditorProps extends CustomCellEditorProps<
  unknown,
  string | string[]
> {
  /** Selection type, single or multiple */
  type?: 'single' | 'multiple';

  /** Enable search input */
  withSearch?: boolean;

  /** Flat options list to render */
  options?: FlatSelectOption[];

  /** The list of options that should not be displayed */
  disabledOptions?: FlatSelectOption[];

  /** Callback when an option is selected */
  onOptionSelect?: (option: FlatSelectOption) => void;
}

export const FlatSelectEditor = ({
  type = 'single',
  withSearch = true,
  options: allOptions = [],
  disabledOptions,
  value,
  onValueChange,
  onOptionSelect,
  api,
}: FlatSelectEditorProps) => {
  const { t } = useTranslation('@mint-lib/ag-grid');

  const options = useMemo(() => {
    const disabledSet = new Set(disabledOptions?.map((option) => option.value));

    return allOptions.filter((option) => !disabledSet.has(option.value));
  }, [allOptions, disabledOptions]);

  const selected = useMemo(() => {
    if (!value) {
      return new Set<string>();
    }

    return new Set(Array.isArray(value) ? value : [value]);
  }, [value]);

  const handleSelectAll = () => {
    const isAllSelected = selected.size === options.length;

    if (isAllSelected) {
      onValueChange([]);
      return;
    }

    onValueChange(options.map((option) => option.value));
  };

  const handleSelect = (option: FlatSelectOption) => {
    if (type === 'single') {
      onValueChange(option.value);
      onOptionSelect?.(option);
      // Close the AG Grid popup cell editor after a single pick.
      api?.stopEditing?.();
      return;
    }

    const newSelected = new Set(selected);
    if (newSelected.has(option.value)) {
      newSelected.delete(option.value);
    } else {
      newSelected.add(option.value);
    }

    onValueChange(Array.from(newSelected));
    onOptionSelect?.(option);
  };

  return (
    <div className="max-w-150 bg-ui-primary">
      <ComboboxMenu
        withSearch={withSearch}
        emptyMessage={t('No options available')}
      >
        {type === 'multiple' && (
          <ComboboxMenuButton
            selectedOptions={Array.from(selected)}
            options={options}
            onSelectAll={handleSelectAll}
            onClearAll={() => onValueChange([])}
          />
        )}

        {options.map((option) => {
          const isSelected = selected.has(option.value);

          return (
            <ComboboxItem
              key={option.value}
              value={option.label}
              selected={isSelected}
              onSelect={() => handleSelect(option)}
              data-dd-action-name="Grid cell select: option"
            >
              {type === 'multiple' && <Checkbox checked={isSelected} />}
              <TruncateText tooltipClassName="break-all whitespace-normal">
                {option.label}
              </TruncateText>
            </ComboboxItem>
          );
        })}
      </ComboboxMenu>
    </div>
  );
};
